import React, { useEffect } from 'react';
import { Else, If, Then } from 'react-if';
import Link from 'next/link';
import { useApolloClient } from '@apollo/client';

import { Box } from '@nerdwallet/currency/components/Box';
import { Text } from '@nerdwallet/currency/components/Text';
import { Title } from '@nerdwallet/currency/components/Title';
import { Button } from '@nerdwallet/nerdwallet-plus-components';
import { LoadingSpinner } from '@nerdwallet/currency/components/LoadingSpinner';

import {
  useEmitRewardableEvent,
  useIsNerdWalletPlusMember,
} from '@lib/hooks/nerdwallet-plus';
import { useInsurancePull } from '@lib/hooks/nerdwallet-plus/use-insurance-pull';
import { InsuranceType } from '@lib/types/insurance';
import { RewardableEventType } from '@lib/types/nerdwallet-plus-rewards';

import { Actions, type Action } from '../canopy-flow-state';
import styles from './found-policies-screen.module.scss';

type Props = {
  dispatch: React.Dispatch<Action>;
  pullId: string;
};

const supportedPolicies = {
  [InsuranceType.Auto]: 'Auto insurance',
  [InsuranceType.Homeowners]: 'Home insurance',
  [InsuranceType.Renters]: 'Renters insurance',
};

const PoliciesFoundScreen = ({ dispatch, pullId }: Props): React.ReactNode => {
  const apolloClient = useApolloClient();
  const [emitReward] = useEmitRewardableEvent();

  const { isNerdWalletPlusMember, loading: nwPlusLoading } =
    useIsNerdWalletPlusMember();
  const { pull, loading, policies, polling } = useInsurancePull(pullId, 0);

  const hasPolicies = policies?.length > 0;
  const hasNoPolicies =
    !pull?.policies?.length && !pull?.unsupportedPolicies?.length;

  const handleContinue = async () => {
    dispatch({
      type: Actions.CHECK_POLICIES_AND_RATES,
      payload: {
        isNerdWalletPlusMember,
        hasSeenFoundPolicies: true,
        noPoliciesFound: hasNoPolicies,
        pullId,
      },
    });

    if (hasNoPolicies || !isNerdWalletPlusMember) {
      apolloClient.refetchQueries({
        updateCache(cache) {
          cache.evict({ fieldName: 'insuranceAccounts' });
        },
      });
    }
  };

  const headerMessage = hasNoPolicies
    ? `We found no policies on your ${pull?.friendlyCarrierName} insurance`
    : `We found these policies on your ${pull?.friendlyCarrierName} insurance`;

  const ctaText = hasNoPolicies ? 'Go back to dashboard' : 'Done';

  useEffect(
    function rewardPoints() {
      if (nwPlusLoading || !hasPolicies) {
        return;
      }

      if (policies.some((policy) => policy.policyType === InsuranceType.Auto)) {
        emitReward(RewardableEventType.LINKED_AUTO_INSURANCE_POLICY);
      }

      if (
        policies.some(
          (policy) =>
            policy.policyType === InsuranceType.Homeowners ||
            policy.policyType === InsuranceType.Renters,
        )
      ) {
        emitReward(RewardableEventType.LINKED_HOME_OR_RENTERS_INSURANCE_POLICY);
      }
    },
    [emitReward, hasPolicies, nwPlusLoading, policies],
  );

  return (
    <Box className={styles.foundPoliciesWrapper}>
      <If condition={loading || polling}>
        <Then>
          <Box
            marginBottom="2"
            display="flex"
            justifyContent="center"
            alignContent={'center'}
          >
            <LoadingSpinner inline size={64} />
          </Box>
        </Then>
        <Else>
          <Box className={styles.copyContainer}>
            <Title size="small" className={styles.title}>
              {headerMessage}
            </Title>
            <If condition={hasNoPolicies}>
              <Then>
                <Box marginY={4}>
                  <Text>
                    Please ensure you have active policies with this provider.
                    Reach out to{' '}
                    <Link href="mailto: support@nerdwallet.com" target="_blank">
                      support@nerdwallet.com
                    </Link>{' '}
                    if you believe this to be an error.
                  </Text>
                </Box>
              </Then>
              <Else>
                <Box marginTop={4}>
                  {pull?.policies?.map((policies, key) => (
                    <Box
                      key={key}
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                      marginTop={1}
                    >
                      <Text bold>
                        {supportedPolicies[policies.policyType] ??
                          policies.policyType}
                      </Text>
                      <Text tone="positive">Linked</Text>
                    </Box>
                  ))}
                  {pull?.unsupportedPolicies?.map((policies, key) => (
                    <Box
                      key={key}
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                      marginTop={1}
                    >
                      <Box maxWidth={{ desktop: '70', mobile: '50' }}>
                        <Text>{policies.friendlyValue}</Text>
                      </Box>
                      <Text align="right">Not supported</Text>
                    </Box>
                  ))}
                </Box>
              </Else>
            </If>
          </Box>
          <Box marginTop="3-half">
            <Button block onClick={handleContinue}>
              {ctaText}
            </Button>
          </Box>
        </Else>
      </If>
    </Box>
  );
};

export default PoliciesFoundScreen;
